import type { XY } from "../engine/xy";
import { GoldPickupEffect } from "../engine/effects/goldPickupEffect";
import type { Camera } from "./camera";
import { dpr } from "./dpr";
import { FloatingPopupRenderer, type FloatingPopupBase } from "./floatingPopupRenderer";

// Lifetime of a single gold popup (ms). Shorter than the loot popup so it
// reads as a subtler, more transient acknowledgement.
export const GOLD_POPUP_DURATION_MS = 1100;
// Fraction of duration at which the popup starts fading out.
const POPUP_FADE_START = 0.8;
// Total pixels the popup floats upward over its lifetime.
const POPUP_FLOAT_DISTANCE = 26;
// Initial Y offset (negative = above the cell center, i.e. above the player's head).
const POPUP_INITIAL_Y_OFFSET = -22;
const POPUP_FONT_SIZE = 12;
// Faint background so the text stays readable on bright tiles.
const POPUP_BG_COLOR = "rgba(0, 0, 0, 0.3)";
// Gold-tinted text — matches the gold sprite tint family.
const POPUP_TEXT_COLOR = "#ffd24d";
const POPUP_PADDING_X = 5;
const POPUP_PADDING_Y = 2;

interface ActivePopup extends FloatingPopupBase {
	anchorXY: XY;
	amount: number;
}

export class GoldPopupRenderer extends FloatingPopupRenderer<ActivePopup> {
	protected durationMs = GOLD_POPUP_DURATION_MS;
	protected fadeStart = POPUP_FADE_START;
	protected floatDistance = POPUP_FLOAT_DISTANCE;

	addPopup(effect: GoldPickupEffect, camera: Camera): void {
		const existing = this.findActivePopupAt(effect.xy);
		if (existing) {
			existing.amount += effect.amount;
			existing.elapsed = 0;
			this.snapToCamera(existing, camera);
			return;
		}

		const popup: ActivePopup = {
			anchorXY: effect.xy,
			amount: effect.amount,
			elapsed: 0,
			screenX: 0,
			screenY: 0,
		};
		this.snapToCamera(popup, camera);
		this.popups.push(popup);
	}

	private findActivePopupAt(xy: XY): ActivePopup | undefined {
		return this.popups.find((p) => p.anchorXY.equals(xy) && p.elapsed < GOLD_POPUP_DURATION_MS);
	}

	private snapToCamera(popup: ActivePopup, camera: Camera): void {
		const center = camera.getCellCenterPixel(popup.anchorXY);
		popup.screenX = center.x;
		popup.screenY = center.y - camera.cellHeight / 2 + POPUP_INITIAL_Y_OFFSET * dpr();
	}

	protected drawContent(
		ctx: CanvasRenderingContext2D,
		popup: ActivePopup,
		yOffset: number,
		ratio: number,
	): void {
		const fontSize = Math.round(POPUP_FONT_SIZE * ratio);
		const paddingX = POPUP_PADDING_X * ratio;
		const paddingY = POPUP_PADDING_Y * ratio;

		ctx.font = `${fontSize}px monospace`;

		const text = `+${popup.amount}`;
		const textWidth = ctx.measureText(text).width;

		const bgWidth = textWidth + paddingX * 2;
		const bgHeight = fontSize + paddingY * 2;
		const bgX = popup.screenX - bgWidth / 2;
		const bgY = popup.screenY + yOffset - bgHeight / 2;

		ctx.fillStyle = POPUP_BG_COLOR;
		ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

		ctx.fillStyle = POPUP_TEXT_COLOR;
		ctx.fillText(text, bgX + paddingX, bgY + paddingY + fontSize);
	}

	// Test helper: snapshot of active popup amounts for the given anchor.
	getActivePopupAmount(xy: XY): number | null {
		const popup = this.findActivePopupAt(xy);
		return popup ? popup.amount : null;
	}

	// Test helper: count of currently active popups.
	getPopupCount(): number {
		return this.popups.length;
	}
}
